"use client"
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { Form, Formik } from "formik"
import { Button } from "@/components/ui/button"
import { FormInput } from "@/components/form-inputs"
import { usePlatformLowBalanceThresholdQuery } from "@/hooks/queries/platform-low-balance-threshold"
import { useUpdatePlatformLowBalanceThresholdMutation } from "@/hooks/mutations/platform-low-balance-threshold"
import { LOW_BALANCE_THRESHOLD_MAX_MICROUSD, MICRO_USD_PER_CREDIT } from "@/lib/billing/constants"
import { parseCreditsToMicroUsd } from "@/lib/billing/utils/credits"
import { useTranslations } from "next-intl"


const MAX_CREDITS = LOW_BALANCE_THRESHOLD_MAX_MICROUSD / MICRO_USD_PER_CREDIT

const DESCRIPTION =
  "Where each tenant starts; tenants can change theirs on their Billing page. The same threshold decides " +
  "when the app shows the low-balance banner. 0 turns low-balance warnings off."

/** Number inputs hand Formik a number, or "" when empty. */
interface ThresholdValues {
  thresholdCredits: number | string
}

/** The platform's default low-balance threshold: a Billing setting, tuned where its notification lives. */
export function LowBalanceThresholdField() {
  const { formatCredits } = useCreditFormatters()

  const tUi = useTranslations("SystemNotifications.SystemNotificationsLowBalanceThresholdField")

  const { data, isLoading, isError } = usePlatformLowBalanceThresholdQuery()
  const update = useUpdatePlatformLowBalanceThresholdMutation()

  if (isLoading) return <p className="text-sm text-muted-foreground"><span key={tUi("loadingTheDefaultThreshold")}>{tUi("loadingTheDefaultThreshold")}</span></p>
  if (isError || !data) return <p className="text-sm text-destructive"><span key={tUi("couldNotLoadTheDefaultThreshold")}>{tUi("couldNotLoadTheDefaultThreshold")}</span></p>

  if (!data.canEdit) {
    return (
      <div className="space-y-1">
        <p className="text-sm">
          <span className="text-muted-foreground"><span key={tUi("defaultThreshold")}>{tUi("defaultThreshold")}</span></span>{" "}
          <span className="font-medium">{formatCredits(data.thresholdMicrousd)}</span>
        </p>
        <p className="text-xs text-muted-foreground"><span key={tUi("changingItNeedsPermissionToManageBillingPlans")}>{tUi("changingItNeedsPermissionToManageBillingPlans")}</span></p>
      </div>
    )
  }

  return (
    <Formik<ThresholdValues>
      enableReinitialize
      // A number, as the input stores: a string seed makes an unchanged value dirty.
      initialValues={{ thresholdCredits: data.thresholdMicrousd / MICRO_USD_PER_CREDIT }}
      validate={validateThreshold}
      onSubmit={(values) =>
        update.mutateAsync(parseCreditsToMicroUsd(values.thresholdCredits) ?? 0).catch(() => {})
      }
    >
      {({ dirty, isSubmitting }) => (
        <Form noValidate className="space-y-2">
          <FormInput
            name="thresholdCredits"
            label={tUi("defaultThresholdCredits")}
            description={DESCRIPTION}
            type="number"
            min={0}
            max={MAX_CREDITS}
            step="any"
            containerClassName="max-w-md"
            disabled={update.isPending}
          />
          {dirty && (
            <Button type="submit" size="sm" disabled={update.isPending || isSubmitting}>
              <span key={tUi("save")}>{tUi("save")}</span></Button>
          )}
        </Form>
      )}
    </Formik>
  )
}

function validateThreshold({ thresholdCredits }: ThresholdValues) {
  const microUsd = parseCreditsToMicroUsd(thresholdCredits)
  if (microUsd === null || microUsd < 0 || microUsd > LOW_BALANCE_THRESHOLD_MAX_MICROUSD) {
    return { thresholdCredits: `Enter a number of credits from 0 to ${MAX_CREDITS.toLocaleString("en-US")}.` }
  }
  return {}
}
